import { useState, useEffect, useCallback } from 'react';
import { Bolsa, CreateBolsaDTO, CreateLineaDTO, UpdateLineaDTO, UpdateBolsaDTO, Estado, LineaDeBolsa } from '../types';
import { BolsasService } from '../services/bolsasService';
import { EstadoBolsa } from '../pages/CentroCotizaciones/state/bolsaMachine';

// Agrupa líneas por producto_id manteniendo orden de inserción dentro de cada grupo (CA-002)
function agruparLineasPorProducto(lineas: LineaDeBolsa[]): LineaDeBolsa[] {
  const grupos = new Map<string, LineaDeBolsa[]>();

  lineas.forEach((linea) => {
    const productoId = linea.producto_id;
    if (!grupos.has(productoId)) {
      grupos.set(productoId, []);
    }
    grupos.get(productoId)!.push(linea);
  });

  const resultado: LineaDeBolsa[] = [];
  grupos.forEach((grupo) => {
    resultado.push(...grupo);
  });

  return resultado;
}

export function useBolsas() {
  const [bolsas, setBolsas] = useState<Bolsa[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBolsaId, setSelectedBolsaId] = useState<string | null>(null);

  // Preserve existing lineas when merging list data (list endpoint returns bolsas without lineas).
  // Without this merge, cargarDatos running after cargarDetalleBolsa would wipe the detail.
  // SPEC-205 RN-004 — si la bolsa seleccionada ya no viene (se convirtió en cotización), la
  // selección pasa a la primera bolsa ACTIVA restante, o a null si no queda ninguna. El setter
  // funcional evita leer un selectedBolsaId obsoleto desde el listener de focus.
  const aplicarListaActiva = useCallback((bolsasData: Bolsa[]) => {
    setBolsas((prev) => {
      const existingMap = new Map(prev.map((b) => [b.id, b]));
      return bolsasData.map((b) => {
        const existing = existingMap.get(b.id);
        return existing?.lineas ? { ...b, lineas: existing.lineas } : b;
      });
    });
    setSelectedBolsaId((actual) =>
      actual && bolsasData.some((b) => b.id === actual) ? actual : bolsasData[0]?.id ?? null
    );
  }, []);

  // SPEC-205 RN-002 — el panel "Bolsas activas" lista solo las bolsas ACTIVA (filtro en servidor).
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bolsasData, estadosData] = await Promise.all([
        BolsasService.listarBolsas({ estado: EstadoBolsa.ACTIVA }),
        BolsasService.getEstados(),
      ]);
      aplicarListaActiva(bolsasData);
      setEstados(estadosData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [aplicarListaActiva]);

  // SPEC-205 RN-004 — refresco silencioso al recuperar el foco: sin indicador de carga y sin
  // vaciar la lista mientras espera; si falla, conserva la lista anterior e informa el error.
  const refrescarBolsas = useCallback(async () => {
    try {
      const bolsasData = await BolsasService.listarBolsas({ estado: EstadoBolsa.ACTIVA });
      aplicarListaActiva(bolsasData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    }
  }, [aplicarListaActiva]);

  // Cargar bolsas y estados al inicializar
  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SPEC-205 RN-004 / UC-006 — el catálogo abierto en otra pestaña se pone al día al volver.
  useEffect(() => {
    window.addEventListener('focus', refrescarBolsas);
    return () => window.removeEventListener('focus', refrescarBolsas);
  }, [refrescarBolsas]);

  const crearBolsa = useCallback(
    async (dto: CreateBolsaDTO) => {
      setLoading(true);
      setError(null);
      try {
        const nuevaBolsa = await BolsasService.crearBolsa(dto);
        setBolsas((prev) => [nuevaBolsa, ...prev]);
        setSelectedBolsaId(nuevaBolsa.id);
        return nuevaBolsa;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error crear bolsa';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // CA-001, CA-005: Actualización optimista + refetch en background
  const agregarProducto = useCallback(
    async (bolsaId: string, productoId: string, cantidad: number, nombreProducto?: string, imagenUrl?: string) => {
      setError(null);
      try {
        // 1. Crear línea optimista (visual inmediato, sin esperar API)
        const lineaOptimista: LineaDeBolsa = {
          id: `optimistic-${Date.now()}-${Math.random()}`,
          producto_id: productoId,
          cantidad,
          precio_unitario: 0,
          subtotal: 0,
          nombre_producto: nombreProducto,
          imagen_url: imagenUrl,
        };

        // 2. Actualizar estado inmediatamente con línea optimista (al inicio para visual feedback)
        setBolsas((prev) =>
          prev.map((b) => {
            if (b.id === bolsaId) {
              const lineasActualizadas = [lineaOptimista, ...(b.lineas || [])];
              return {
                ...b,
                lineas: agruparLineasPorProducto(lineasActualizadas),
              };
            }
            return b;
          })
        );

        // 3. Llamar API en background (sin setLoading para no bloquear UI)
        const { bolsa } = await BolsasService.agregarProducto(bolsaId, {
          producto_id: productoId,
          cantidad,
          imagen_url: imagenUrl,
        });

        // 4. Refetch SOLO DE LA BOLSA COMPLETA para sincronizar (obtener precios reales, etc)
        const bolsaActualizada = await BolsasService.obtenerBolsa(bolsaId);
        setBolsas((prev) =>
          prev.map((b) => {
            if (b.id === bolsaId) {
              const lineasAgrupadas = agruparLineasPorProducto(bolsaActualizada.lineas || []);
              // Invertir orden: más nuevas al inicio para visual feedback
              const lineasInvertidas = lineasAgrupadas.reverse();
              return {
                ...bolsaActualizada,
                lineas: lineasInvertidas,
              };
            }
            return b;
          })
        );

        return bolsa;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error agregar producto';
        setError(message);
        throw err;
      }
    },
    []
  );

  const cambiarCantidad = useCallback(
    async (bolsaId: string, lineaId: string, nuevaCantidad: number) => {
      setLoading(true);
      setError(null);
      try {
        const bolsaActualizada = await BolsasService.cambiarCantidad(bolsaId, lineaId, {
          cantidad: nuevaCantidad,
        });
        setBolsas((prev) =>
          prev.map((b) => {
            if (b.id === bolsaId) {
              const lineasAgrupadas = agruparLineasPorProducto(bolsaActualizada.lineas || []);
              return { ...bolsaActualizada, lineas: lineasAgrupadas };
            }
            return b;
          })
        );
        return bolsaActualizada;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error cambiar cantidad';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // CA-001..CA-004: eliminación optimista del producto + refetch de total, rollback en error
  const eliminarProducto = useCallback(
    async (bolsaId: string, lineaId: string) => {
      setError(null);
      const prevBolsas = bolsas; // snapshot para rollback
      // 1. Remoción optimista de la línea + recálculo de total
      setBolsas((prev) =>
        prev.map((b) => {
          if (b.id !== bolsaId) return b;
          const lineas = (b.lineas || []).filter((l) => l.id !== lineaId);
          const total = lineas.reduce((acc, l) => acc + (l.subtotal || 0), 0);
          return { ...b, lineas, total };
        })
      );
      try {
        await BolsasService.eliminarProducto(bolsaId, lineaId);
        // 2. Sincronizar con backend (total real)
        const bolsaActualizada = await BolsasService.obtenerBolsa(bolsaId);
        setBolsas((prev) =>
          prev.map((b) =>
            b.id === bolsaId
              ? {
                  ...bolsaActualizada,
                  lineas: agruparLineasPorProducto(bolsaActualizada.lineas || []),
                }
              : b
          )
        );
      } catch (err) {
        setBolsas(prevBolsas); // rollback
        const message = err instanceof Error ? err.message : 'Error eliminar producto';
        setError(message);
        throw err;
      }
    },
    [bolsas]
  );

  // CA-007..CA-010: eliminación optimista de bolsa + reasignación de selección, rollback en error
  const eliminarBolsa = useCallback(
    async (bolsaId: string) => {
      setError(null);
      const prevBolsas = bolsas;
      const prevSelected = selectedBolsaId;
      // 1. Remoción optimista del listado
      const restantes = bolsas.filter((b) => b.id !== bolsaId);
      setBolsas(restantes);
      // 2. Reasignar selección si se borró la seleccionada (CA-009)
      if (selectedBolsaId === bolsaId) {
        setSelectedBolsaId(restantes.length > 0 ? restantes[0].id : null);
      }
      try {
        await BolsasService.eliminarBolsa(bolsaId);
      } catch (err) {
        // 3. Rollback (CA-010)
        setBolsas(prevBolsas);
        setSelectedBolsaId(prevSelected);
        const message = err instanceof Error ? err.message : 'Error eliminar bolsa';
        setError(message);
        throw err;
      }
    },
    [bolsas, selectedBolsaId]
  );

  const actualizarNombre = useCallback(
    async (bolsaId: string, nuevoNombre: string) => {
      setLoading(true);
      setError(null);
      try {
        const bolsaActualizada = await BolsasService.actualizarBolsa(bolsaId, {
          nombre: nuevoNombre,
        });
        setBolsas((prev) => prev.map((b) => (b.id === bolsaId ? bolsaActualizada : b)));
        return bolsaActualizada;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error actualizar nombre';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const guardarBorrador = useCallback(
    async (bolsaId: string) => {
      setLoading(true);
      setError(null);
      try {
        const bolsaActualizada = await BolsasService.guardarBorrador(bolsaId);
        setBolsas((prev) => prev.map((b) => (b.id === bolsaId ? bolsaActualizada : b)));
        return bolsaActualizada;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error guardar borrador';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const continuarCotizacion = useCallback(async (bolsaId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await BolsasService.continuarCotizacion(bolsaId);
      setBolsas((prev) => prev.map((b) => (b.id === bolsaId ? result.bolsa : b)));
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error continuar cotización';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedBolsa = selectedBolsaId ? bolsas.find((b) => b.id === selectedBolsaId) : null;

  // Cargar detalles de bolsa cuando se selecciona
  const cargarDetalleBolsa = useCallback(
    async (bolsaId: string) => {
      try {
        const bolsaDetalle = await BolsasService.obtenerBolsa(bolsaId);
        const lineasAgrupadas = agruparLineasPorProducto(bolsaDetalle.lineas || []);
        setBolsas((prev) =>
          prev.map((b) => (b.id === bolsaId ? { ...bolsaDetalle, lineas: lineasAgrupadas } : b))
        );
      } catch (err) {
        console.error('Error cargando detalle de bolsa:', err);
      }
    },
    []
  );

  const handleSelectBolsa = useCallback(
    async (bolsaId: string) => {
      setSelectedBolsaId(bolsaId);
      await cargarDetalleBolsa(bolsaId);
    },
    [cargarDetalleBolsa]
  );

  const agregarProductoABolsaActiva = useCallback(
    async (productoId: string, cantidad: number, nombreProducto?: string, imagenUrl?: string) => {
      if (!selectedBolsaId) {
        throw new Error('No hay bolsa activa seleccionada');
      }
      return agregarProducto(selectedBolsaId, productoId, cantidad, nombreProducto, imagenUrl);
    },
    [selectedBolsaId, agregarProducto]
  );

  return {
    bolsas,
    estados,
    loading,
    error,
    selectedBolsaId,
    selectedBolsa,
    setSelectedBolsaId,
    handleSelectBolsa,
    crearBolsa,
    agregarProducto,
    agregarProductoABolsaActiva,
    cambiarCantidad,
    eliminarProducto,
    eliminarBolsa,
    actualizarNombre,
    guardarBorrador,
    continuarCotizacion,
    cargarDatos,
    agruparLineasPorProducto,
  };
}
